/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Console sidebar. Organised by product (QuickPods, TrainPods, Inference),
// then Storage and Account. Three widths:
//   > 1100px  full 232px sidebar with group labels
//   821–1100  76px icon rail
//   ≤ 820px   hidden; MobileBar's menu button opens it full-width
import React from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getConsoleNav, ConsoleNavItem } from '../../config/consoleNav';
import { UsersIc } from '../console/icons';
import { useSidebar } from '../../contexts/SidebarContext';
import { assignmentApi } from '../../services/assignmentApi';

const itemBase =
  'flex items-center gap-2.5 min-h-[36px] px-2.5 rounded-md text-sm transition-colors ' +
  'min-[821px]:max-[1100px]:justify-center min-[821px]:max-[1100px]:w-12 min-[821px]:max-[1100px]:min-h-[44px] min-[821px]:max-[1100px]:px-0 ' +
  'max-[820px]:min-h-[44px] max-[820px]:text-[15px]';
const labelCls = 'min-[821px]:max-[1100px]:sr-only truncate';

export const Logo: React.FC<{ nameClassName?: string }> = ({ nameClassName = '' }) => (
  <>
    <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" aria-hidden="true" className="w-7 h-7 flex-none" />
    <span className={`font-display font-semibold text-[17px] text-ps-text whitespace-nowrap ${nameClassName}`}>Podstack Cloud</span>
  </>
);

const NavItem: React.FC<{ item: ConsoleNavItem; active: boolean }> = ({ item, active }) => {
  const Icon = item.icon;
  if (item.external) {
    return (
      <a
        id={item.id}
        href={item.href}
        title={item.name}
        className={`${itemBase} text-ps-muted hover:bg-ps-hover hover:text-ps-text`}
      >
        <Icon />
        <span className={labelCls}>{item.name}</span>
      </a>
    );
  }
  return (
    <NavLink
      id={item.id}
      to={item.href}
      end={item.end}
      title={item.name}
      aria-current={active ? 'page' : undefined}
      className={`${itemBase} ${active ? 'bg-ps-accent-soft text-ps-accent' : 'text-ps-muted hover:bg-ps-hover hover:text-ps-text'}`}
    >
      <Icon />
      <span className={labelCls}>{item.name}</span>
    </NavLink>
  );
};

const Sidebar: React.FC = () => {
  const { isOpen } = useSidebar();
  const { pathname } = useLocation();
  const groups = getConsoleNav();

  // "Assigned resources" only appears once the user has at least one assignment.
  const hasAssignments = useQuery({
    queryKey: ['has-any-assignments'],
    queryFn: async () => {
      try {
        const p = await assignmentApi.listPods();
        if (p.meta.total > 0) return true;
        const h = await assignmentApi.listHelm();
        return h.meta.total > 0;
      } catch {
        return false;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const isActive = (item: ConsoleNavItem) => {
    if (item.external) return false;
    if (item.end) return pathname === item.href;
    return [item.href, ...(item.match || [])].some((p) => pathname === p || pathname.startsWith(p + '/'));
  };

  return (
    <nav
      id="console-nav"
      aria-label="Primary"
      className={
        'bg-ps-chrome border-ps-raised flex flex-col gap-0.5 overflow-y-auto ' +
        // desktop
        'min-[1101px]:sticky min-[1101px]:top-0 min-[1101px]:h-screen min-[1101px]:w-[232px] min-[1101px]:flex-none min-[1101px]:border-r min-[1101px]:px-3 min-[1101px]:pt-4 min-[1101px]:pb-6 ' +
        // tablet rail
        'min-[821px]:max-[1100px]:sticky min-[821px]:max-[1100px]:top-0 min-[821px]:max-[1100px]:h-screen min-[821px]:max-[1100px]:w-[76px] min-[821px]:max-[1100px]:flex-none min-[821px]:max-[1100px]:border-r min-[821px]:max-[1100px]:items-center min-[821px]:max-[1100px]:px-2.5 min-[821px]:max-[1100px]:py-4 ' +
        // phone
        (isOpen ? 'max-[820px]:flex max-[820px]:w-full max-[820px]:border-b max-[820px]:px-4 max-[820px]:pt-2 max-[820px]:pb-4' : 'max-[820px]:hidden')
      }
    >
      <Link
        to="/"
        aria-label="Podstack Cloud home"
        className="max-[820px]:hidden flex items-center gap-2.5 px-2.5 pt-1 pb-3.5 min-[821px]:max-[1100px]:px-0"
      >
        <Logo nameClassName="min-[821px]:max-[1100px]:hidden" />
      </Link>

      {groups.map((g) => (
        <React.Fragment key={g.id}>
          {g.label && (
            <div className="mx-2.5 mt-3.5 mb-1 text-[11px] tracking-[0.08em] uppercase text-ps-subtle font-semibold min-[821px]:max-[1100px]:text-[0px] min-[821px]:max-[1100px]:h-px min-[821px]:max-[1100px]:w-8 min-[821px]:max-[1100px]:bg-ps-border min-[821px]:max-[1100px]:mx-0 min-[821px]:max-[1100px]:mt-2.5 min-[821px]:max-[1100px]:mb-1.5">
              {g.label}
              {g.hint && <span className="normal-case tracking-normal font-normal"> · {g.hint}</span>}
            </div>
          )}
          {g.items.map((item) => (
            <NavItem key={item.id} item={item} active={isActive(item)} />
          ))}
        </React.Fragment>
      ))}

      {hasAssignments.data && (
        <NavItem
          item={{ id: 'nav-assignments', name: 'Assigned resources', href: '/assignments', icon: UsersIc }}
          active={pathname.startsWith('/assignments')}
        />
      )}
    </nav>
  );
};

export default Sidebar;
